import MenuIcon from "@mui/icons-material/Menu";
import {
  Toolbar,
  Typography,
  CssBaseline,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Button,
  Drawer,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { ReactNode, useRef } from "react";

import { DrawerMenuItemsType } from "./menu-items";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Worksheets, { WorksheetMethods } from "../components/worksheets";
import OrionLogo from "../assets/orion-logo.png";
import { useLoading } from "../hooks/loadingcontext";

// const LinkLogo = require("../assets/LinkLogo.svg");

export interface UserProfileMenuOptions {
  text?: string;
  onMenuClick?: () => void;
  iconComponent?: React.JSX.Element;
}

const drawerWidth = 180;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

export default function Header({
  children,
  drawerMenuItems,
  userProfileProps,
  userProfileMenuItems,
}: {
  children: ReactNode;
  drawerMenuItems: DrawerMenuItemsType[];
  userProfileProps: {
    firstName: string;
    profileURL: string;
  };
  userProfileMenuItems?: UserProfileMenuOptions[];
}) {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const worksheetRef = useRef<WorksheetMethods>(null);
  const { isLoading, setIsLoading } = useLoading();

  const openRightMenu = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleLogoClick = () => {
    if (isLoading) {
      return;
    }
    worksheetRef.current?.worksheetCreateMethod();
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        open={open}
        color="inherit"
        elevation={0}
        sx={{
          height: "50px",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              mr: 2,
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon sx={{ color: "secondary.light" }} />
          </IconButton>
          <Box
            sx={{
              display: "flex",
              width: "fill-available",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                component="img"
                src={OrionLogo}
                alt="Responsive"
                sx={{
                  width: "60px",
                  height: "auto",
                  maxWidth: "100%",
                  objectFit: "contain",
                  cursor: "pointer",
                  transform: "scale(1.2)",
                }}
                onClick={handleLogoClick}
              ></Box>
            </Box>

            <Button
              id="profile-button"
              aria-controls={open ? "profile-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              variant="outlined"
              disableElevation
              onClick={handleClick}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                textTransform: "none",
                borderRadius: "20px",
                borderColor: "gray",
                color: "black",
              }}
            >
              <Typography
                noWrap
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  maxWidth: "150px",
                }}
              >
                {userProfileProps.firstName}
              </Typography>
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <Worksheets
          ref={worksheetRef}
          handleDrawerClose={handleDrawerClose}
        ></Worksheets>
      </Drawer>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={openRightMenu}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {userProfileMenuItems.map((profileMenuItem) => (
          <MenuItem
            onClick={profileMenuItem.onMenuClick}
            key={profileMenuItem.text}
          >
            {profileMenuItem.iconComponent} {profileMenuItem.text}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
